import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useCallback, useMemo } from 'react';

import {
    changeQbooksAccountLineItemsAllChecked,
    cloneQbooksAccountLineItems,
    cloneQbooksAccountLineItemsAll,
    removeQbooksAccountLineItems,
    removeQbooksAccountLineItemsAll,
} from '../../../../actions';
import { getContext } from '../../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../../selectors/qbooks';
import { ExpandedQBooksAccountLineItem } from '../../../../selectors/types/ExpandedQBooksAccountLineItem';
import { useRequestExpanded } from './useRequest';

export const useTableBulkActions = (accountLineItems: ExpandedQBooksAccountLineItem[]) => {
    const dispatch = useDispatch();
    const request = useRequestExpanded();
    const context = useSelector(getQBooksContext);
    const isQBooksAccountReadonly = useIsQBooksAccountReadonly();

    const withTax = request.details.lineAmountType !== domain.LineAmountType.NoTax;
    const withClass = context.hasClassFeature;
    const withCustomer = context.hasCustomerFeature;

    const itemsChecked = useMemo(() => accountLineItems.filter((item) => item.checked), [accountLineItems]);
    const isAllItemsChecked = itemsChecked.length === accountLineItems.length;

    /**
     * Handler to uncheck all line items
     */
    const handleCancelBulkActions = useCallback(() => {
        dispatch(changeQbooksAccountLineItemsAllChecked(false));
    }, [dispatch]);

    /**
     * Handler to clone checked line items
     */
    const handleBulkClone = useCallback(() => {
        if (isAllItemsChecked) {
            dispatch(cloneQbooksAccountLineItemsAll());
        } else {
            dispatch(cloneQbooksAccountLineItems(itemsChecked.map((item) => item.id)));
        }

        dispatch(changeQbooksAccountLineItemsAllChecked(false));
    }, [dispatch, isAllItemsChecked, itemsChecked]);

    /**
     * Handler to remove checked line items
     */
    const handleBulkRemove = useCallback(() => {
        if (isAllItemsChecked) {
            dispatch(removeQbooksAccountLineItemsAll());
        } else {
            dispatch(removeQbooksAccountLineItems(itemsChecked.map((lineItem) => lineItem.id)));
        }
    }, [dispatch, isAllItemsChecked, itemsChecked]);

    const withBulkClone = useMemo(() => itemsChecked.every((item) => !item.hideClone), [itemsChecked]);
    const withBulkRemove = useMemo(() => itemsChecked.every((item) => !item.hideRemove), [itemsChecked]);
    const withBulkEdit = !isQBooksAccountReadonly || withTax || withClass || withCustomer;

    return {
        withBulkEdit,
        withBulkClone,
        withBulkRemove,
        handleBulkClone,
        handleBulkRemove,
        handleCancelBulkActions,
    };
};

const useIsQBooksAccountReadonly = () => {
    const context = useSelector(getContext);

    return useMemo(() => {
        const qBooksItemField = context.fields.find(
            (field) => field.systemPurpose === domain.FieldSystemPurpose.QBooksAccount
        );

        return Boolean(qBooksItemField && context.readonlyFieldIds.includes(qBooksItemField.id));
    }, [context.fields, context.readonlyFieldIds]);
};
